import { type JSX, useId, useRef, useState } from 'react';

import { defaultMenuGroupKey, menuActions, menuGroups } from '../navigation/site-navigation';
import { useI18n } from '../i18n';
import { useContentOverride } from '../content/ContentProvider';
import { DestinationLink, PageContainer } from './primitives';
import { useOverlay, useScrollLock } from './overlay';

/**
 * The full-platform menu.
 *
 * Just Treated is wide — a shop, clinical services, a fulfilment network, a
 * partner side and an operating system for pharmacies — and almost none of it
 * fits in a header. This is where the whole map lives.
 *
 * ## Tabs, not one long list
 *
 * Roughly sixty destinations in a flat column is not navigation, it is a wall.
 * Grouping them into five means a visitor reads five words, then twelve, and
 * the audiences stay separate: someone shopping for hay fever tablets should
 * never have to scroll past "SOP & Compliance" to find them.
 *
 * On desktop all five groups are visible at once — hover-free, no hidden
 * state, everything scannable. Below the breakpoint the tabs become real
 * tabs, because five columns on a phone is one unreadable column.
 *
 * ## Every destination is honest
 *
 * The links come from `site-navigation`, the same declaration the footer
 * renders, and they go through `DestinationLink`. Most of this platform is not
 * built; those entries render as disabled controls that say "coming soon"
 * rather than as links that appear to work. There is no `href="#"` here.
 */

export function MegaMenu({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}): JSX.Element | null {
  const { t } = useI18n();
  const edited = useContentOverride();
  const [activeKey, setActiveKey] = useState(defaultMenuGroupKey);

  /**
   * An edited name wins over the translated one.
   *
   * The dictionary holds the shipped name in each language; the content block
   * holds what an editor typed. When nothing has been typed the block returns
   * the same string the dictionary does, so the two agree by default.
   */
  const groupName = (group: { titleKey: Parameters<typeof t>[0]; contentKey: string }): string =>
    edited(group.contentKey as never) ?? t(group.titleKey);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useId();

  useOverlay({ open, onClose, containerRef: panelRef, initialFocusRef: closeRef });
  useScrollLock(open);

  if (!open) return null;

  return (
    <div className="jt-menu" role="presentation">
      <div
        className="jt-menu__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panelRef}
      >
        <PageContainer>
          <div className="jt-menu__head">
            <h2 className="jt-menu__title" id={titleId}>
              {edited('menu.title') ?? t('nav.menuTitle')}
            </h2>
            <button
              type="button"
              className="jt-menu__close"
              onClick={onClose}
              aria-label={t('nav.closeMenu')}
              ref={closeRef}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" width="20" height="20">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/*
            Tabs on small screens only. The list keeps its tab semantics at
            every width so the accessibility tree does not change shape with
            the viewport; CSS reveals all panels at once on desktop.
          */}
          <div className="jt-menu__tabs" role="tablist" aria-label={t('nav.menuTitle')}>
            {menuGroups.map((group) => (
              <button
                key={group.key}
                type="button"
                role="tab"
                id={`jt-tab-${group.key}`}
                aria-controls={`jt-panel-${group.key}`}
                aria-selected={group.key === activeKey}
                tabIndex={group.key === activeKey ? 0 : -1}
                className={`jt-menu__tab ${group.key === activeKey ? 'jt-menu__tab--on' : ''}`}
                onClick={() => setActiveKey(group.key)}
              >
                {groupName(group)}
              </button>
            ))}
          </div>

          <div className="jt-menu__body">
            <div className="jt-menu__groups">
              {menuGroups.map((group) => (
                <section
                  key={group.key}
                  id={`jt-panel-${group.key}`}
                  role="tabpanel"
                  aria-labelledby={`jt-tab-${group.key}`}
                  className={`jt-menu__group ${group.key === activeKey ? 'jt-menu__group--on' : ''}`}
                >
                  <h3 className="jt-menu__heading">{groupName(group)}</h3>
                  <ul className="jt-menu__links">
                    {group.links.map((link) => (
                      <li key={link.path}>
                        <DestinationLink destination={link} className="jt-menu__link">
                          {link.label}
                        </DestinationLink>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>

            {/*
              The supply side. Easy to lose inside a consumer menu, and it is
              where the marketplace's revenue comes from — so it gets its own
              panel rather than a row in a column.
            */}
            <aside className="jt-menu__actions" aria-label={t('menu.actionsTitle')}>
              <h3 className="jt-menu__actions-title">
                {edited('menu.actions-title') ?? t('menu.actionsTitle')}
              </h3>
              <p className="jt-menu__actions-lede">{edited('menu.actions-lede') ?? t('menu.actionsLede')}</p>
              <ul className="jt-menu__actions-list">
                {menuActions.map((action) => (
                  <li key={action.path}>
                    <DestinationLink destination={action} className="jt-menu__action">
                      {action.label}
                      <span aria-hidden="true">→</span>
                    </DestinationLink>
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </PageContainer>
      </div>
    </div>
  );
}
